import {
  ICinemaPalette,
  withAlpha,
} from '../DashboardContent/MytaStreams/LoadingCinema/palette';
import {
  BRANDING_TRANSITION_BLUR_CLASS,
  BRANDING_TRANSITION_FADE_SEC,
  BRANDING_TRANSITION_MS,
} from './constants';

const MOBILE = '@media (max-width: 900px)';

const STEP_DOT = {
  width: '8px',
  height: '8px',
  flex: 'none',
  borderRadius: '50%',
};

const HALO = '0 1px 2px rgba(4,6,9,.55), 0 2px 22px rgba(4,6,9,.45)';
const SOFT_HALO = '0 1px 2px rgba(4,6,9,.5)';
const MONO = "'JetBrains Mono', monospace";
const DISP = "'Space Grotesk', sans-serif";
const BODY = "'Inter', sans-serif";
const INK_DIM = 'rgba(244,246,250,.72)';

export const getBrandingTransitionStyles = (palette: ICinemaPalette) => ({
  root: {
    position: 'fixed',
    inset: 0,
    // Above the editor overlay (1200) and any dialog it opened (MUI modal 1300), below the AFK
    // prompt (1500) — a seat about to be taken away outranks a re-skin.
    zIndex: 1400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    fontFamily: BODY,
    color: '#f4f6fa',
  },
  // Frosted rather than opaque: the stream keeps playing underneath, and the blur is what hides the
  // half-dressed map — spots popping in one by one — that this screen exists to cover.
  //
  // The stream's own blur is NOT done here: `backdrop-filter` cannot be trusted to sample a video
  // surface (it samples nothing at all over the stream in Edge, which left the map perfectly sharp
  // behind a scrim). The video is blurred directly instead — see `brandingStreamBlurGlobalStyles` —
  // and this one is left only as much blur as the hub's own DOM chrome behind the cover needs.
  backdrop: {
    position: 'absolute',
    inset: 0,
    backdropFilter: 'blur(10px) saturate(1.05)',
    WebkitBackdropFilter: 'blur(10px) saturate(1.05)',
    background:
      'radial-gradient(120% 120% at 50% 45%, rgba(8,11,16,.52) 0%, rgba(4,6,9,.72) 70%, rgba(4,6,9,.8) 100%)',
  },
  card: {
    position: 'relative',
    width: 'min(560px, calc(100vw - 48px))',
    padding: '30px 34px 26px',
    borderRadius: '18px',
    border: '1px solid rgba(244,246,250,.14)',
    background:
      'linear-gradient(135deg, rgba(28,28,34,.72) 0%, rgba(16,16,20,.72) 100%)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    boxShadow: '0 24px 70px rgba(0,0,0,.5)',
    animation: 'mytaBrandingRise .4s cubic-bezier(.16,1,.3,1) both',
    [MOBILE]: { padding: '24px 22px 20px' },
  },
  kicker: {
    display: 'block',
    fontFamily: MONO,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '.24em',
    textTransform: 'uppercase',
    color: palette.accentHi,
    textShadow: HALO,
    marginBottom: '10px',
  },
  headline: {
    fontFamily: DISP,
    fontWeight: 600,
    fontSize: '30px',
    letterSpacing: '-.02em',
    lineHeight: 1.08,
    color: '#fff',
    textShadow: HALO,
    [MOBILE]: { fontSize: '23px' },
  },
  detail: {
    color: 'rgba(255,255,255,.86)',
    fontSize: '14px',
    lineHeight: 1.5,
    marginTop: '10px',
    textShadow: SOFT_HALO,
    [MOBILE]: { fontSize: '13px' },
  },
  stepLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    marginTop: '24px',
    marginBottom: '13px',
  },
  stepDot: {
    ...STEP_DOT,
    background: palette.accentHi,
    boxShadow: `0 0 10px 2px ${palette.accentGlow}`,
    animation: 'mytaBrandingPulse 1.7s ease-in-out infinite',
  },
  // Still lit, no longer breathing: the pulse is what says "working", and leaving it running under
  // a button that offers to leave would keep the screen asking to be waited for.
  stepDotDone: {
    ...STEP_DOT,
    background: palette.accentHi,
    boxShadow: `0 0 10px 2px ${palette.accentGlow}`,
  },
  stepName: {
    fontFamily: MONO,
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '.15em',
    textTransform: 'uppercase',
    color: '#fff',
    textShadow: HALO,
  },
  barTrack: {
    position: 'relative',
    height: '4px',
    borderRadius: '999px',
    overflow: 'hidden',
    background: 'rgba(244,246,250,.14)',
  },
  barFill: {
    position: 'absolute',
    inset: 0,
    transformOrigin: 'left center',
    borderRadius: '999px',
    background: `linear-gradient(90deg, ${withAlpha(
      palette.accentHi,
      0.65,
    )} 0%, ${palette.accentHi} 100%)`,
    boxShadow: `0 0 12px 1px ${palette.accentGlow}`,
    animation: `mytaBrandingFill ${BRANDING_TRANSITION_MS}ms linear forwards`,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '18px',
    marginTop: '14px',
    [MOBILE]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '14px',
    },
  },
  hint: {
    color: INK_DIM,
    fontSize: '12.5px',
    lineHeight: 1.5,
    textShadow: SOFT_HALO,
  },
  dismiss: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 0,
    borderRadius: '11px',
    padding: '11px 22px',
    background: palette.buttonBackgroundColor,
    color: palette.ctaInk,
    fontFamily: DISP,
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '.01em',
    cursor: 'pointer',
    boxShadow: `0 8px 24px ${palette.accentGlow}, 0 1px 0 rgba(255,255,255,.12) inset`,
    transition: 'background .15s, transform .15s, box-shadow .15s',
    animation: 'mytaBrandingRise .3s cubic-bezier(.16,1,.3,1) both',
    '&:hover': {
      background: palette.buttonHoverColor,
      transform: 'translateY(-1px)',
      boxShadow: `0 12px 30px ${palette.accentGlow}`,
    },
    '&:focus-visible': {
      outline: `2px solid ${palette.accentHi}`,
      outlineOffset: '3px',
    },
    '& svg': { width: '16px', height: '16px' },
  },
});

// Every surface a provider streams the world into: myta-streams and CoreWeave hand their library a
// wrapper div, GameLift Streams is given the video tag itself.
const STREAM_SURFACES = [
  '#mytaStreamsWrapper',
  '#coreweaveWrapper',
  '#gamecastVideoTag',
];

/**
 * Blurs the live stream itself while the cover is up, instead of relying on the cover's
 * `backdrop-filter` to do it.
 *
 * A video is composited on a path `backdrop-filter` does not reliably read back from — in Edge the
 * filter is a no-op over the stream, so the map stayed sharp under the scrim and the whole point of
 * the screen (hiding the re-skin) was lost. A plain `filter` on the element that carries the video
 * has no such caveat. Blurring the element also fades its edges towards transparent; the scrim above
 * is near-opaque out there, so it reads as a vignette.
 *
 * Mounted unconditionally and driven by a class on <body> so the rule — and with it the transition —
 * still exists while the cover fades out; defining it only while the cover is mounted would snap the
 * world back into focus a frame before it is gone.
 */
export const brandingStreamBlurGlobalStyles = {
  [STREAM_SURFACES.join(', ')]: {
    transition: `filter ${BRANDING_TRANSITION_FADE_SEC}s ease`,
  },
  [STREAM_SURFACES.map(
    (surface) => `body.${BRANDING_TRANSITION_BLUR_CLASS} ${surface}`,
  ).join(', ')]: {
    filter: 'blur(26px) saturate(1.05)',
  },
};

export const brandingTransitionGlobalStyles = {
  '@keyframes mytaBrandingPulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.4, transform: 'scale(.72)' },
  },
  '@keyframes mytaBrandingRise': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes mytaBrandingFill': {
    from: { transform: 'scaleX(0)' },
    to: { transform: 'scaleX(1)' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '.myta-branding-transition *, .myta-branding-transition *::before, .myta-branding-transition *::after':
      {
        animation: 'none !important',
        transition: 'none !important',
      },
  },
};
